import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  GridProps,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { ReactElement } from 'react';
import { DescribedSelectionProps } from './schema';

type OptionValue = string | number | boolean;

type Option = {
  /** Stable string key for DOM ids and the radio group value. */
  key: string;
  value: OptionValue;
  title: string;
  description?: string;
};

const COLUMN_WIDTHS = { 2: 6, 3: 4, 4: 3 } as const;

const toOptions = (optionSchema: unknown): Option[] => {
  if (typeof optionSchema !== 'object' || optionSchema === null) {
    return [];
  }

  if ('oneOf' in optionSchema && Array.isArray(optionSchema.oneOf)) {
    return (
      optionSchema.oneOf as Array<{
        const: OptionValue;
        title?: string;
        description?: string;
      }>
    ).map(option => ({
      key: String(option.const),
      value: option.const,
      title: option.title ?? String(option.const),
      description: option.description,
    }));
  }

  const enumNames =
    'enumNames' in optionSchema && Array.isArray(optionSchema.enumNames)
      ? (optionSchema.enumNames as string[])
      : [];
  const enumValues =
    'enum' in optionSchema && Array.isArray(optionSchema.enum)
      ? (optionSchema.enum as OptionValue[])
      : [];

  return enumValues.map((value, index) => ({
    key: String(value),
    value,
    title: enumNames[index] ?? String(value),
  }));
};

export const DescribedSelection = (props: DescribedSelectionProps) => {
  const {
    onChange,
    formData,
    schema,
    uiSchema,
    required,
    rawErrors,
    idSchema,
    registry,
    disabled,
    readonly,
  } = props;
  const { DescriptionFieldTemplate, TitleFieldTemplate } = registry.templates;
  const isMultiSelect = schema.type === 'array';
  const options = toOptions(
    isMultiSelect && schema.items && !Array.isArray(schema.items)
      ? schema.items
      : schema,
  );
  const selectedValues: OptionValue[] = Array.isArray(formData) ? formData : [];
  const inline = uiSchema['ui:options']?.inline ?? false;
  const columns = uiSchema['ui:options']?.columns;
  const isDisabled = disabled || readonly;

  let gridItemProps: GridProps = { xs: 12 };
  if (inline) {
    // Without an explicit column count the options size to their content and
    // wrap only once the row is full.
    gridItemProps = columns
      ? { xs: 12, sm: 6, md: COLUMN_WIDTHS[columns] }
      : {};
  }

  const toggleValue = (option: Option, checked: boolean) => {
    const next = checked
      ? [...selectedValues, option.value]
      : selectedValues.filter(value => value !== option.value);
    // Emit in schema order so the output does not depend on click order.
    onChange(options.map(o => o.value).filter(value => next.includes(value)));
  };

  const renderOption = (option: Option, control: ReactElement) => (
    <Box mb={1}>
      {option.description && (
        <Box mb={0.25}>
          <DescriptionFieldTemplate
            id={`${idSchema.$id}_${option.key}__description`}
            description={option.description}
            schema={schema}
            registry={registry}
          />
        </Box>
      )}
      <FormControlLabel control={control} label={option.title} />
    </Box>
  );

  const renderOptions = (control: (option: Option) => ReactElement) => (
    <Grid container spacing={inline ? 2 : 0}>
      {options.map(option => (
        <Grid item key={option.key} {...gridItemProps}>
          {renderOption(option, control(option))}
        </Grid>
      ))}
    </Grid>
  );

  return (
    <FormControl
      component="fieldset"
      required={required}
      error={!!rawErrors?.length}
      disabled={isDisabled}
      fullWidth
    >
      {schema.title && (
        <TitleFieldTemplate
          id={`${idSchema.$id}__title`}
          title={schema.title}
          schema={schema}
          uiSchema={uiSchema}
          required={required}
          registry={registry}
        />
      )}
      {schema.description && (
        <DescriptionFieldTemplate
          id={`${idSchema.$id}__description`}
          description={schema.description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      )}
      <Box mt={schema.description ? 1 : 0}>
        {isMultiSelect ? (
          <FormGroup>
            {renderOptions(option => (
              <Checkbox
                checked={selectedValues.includes(option.value)}
                disabled={isDisabled}
                onChange={event => toggleValue(option, event.target.checked)}
                name={option.key}
              />
            ))}
          </FormGroup>
        ) : (
          <RadioGroup
            value={formData === undefined ? '' : String(formData)}
            onChange={event =>
              onChange(
                options.find(option => option.key === event.target.value)?.value,
              )
            }
          >
            {renderOptions(option => (
              <Radio value={option.key} disabled={isDisabled} />
            ))}
          </RadioGroup>
        )}
      </Box>
    </FormControl>
  );
};
